import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { State } from '@progress/kendo-data-query';
import { PtLuxonDate } from '../../models/projects/date.models';
import { DailyLogOperationInstance } from '../../models/projects/operation.models';
import { JackingActivityEditor, JackingActivityTypes } from '../../models/projects/jacking-activity.models';
import { DbContext, DbTable } from '../data/dbcontext.service';
import { DateService } from '../date/date.service';
import { VesselService } from '../vessel/vessel.service';
import { TimeZoneService } from '../time-zone/time-zone.service';
import {
  FieldPositioningEditor,
  FieldPositioningLegSummaryEditor,
  FieldPositioningLegSummaryPerLegEditor,
  MAX_FIELD_POSITIONING_LEGS,
  normalizeFieldPositioningLegSummary
} from '../../models/field-positioning/field-positioning.models';
import { SetupService } from '../setup/setup.service';
import { ErrorType, ValidationResult } from '../../models/projects/validation.models';
import { AuthService } from '../../_foundation/services/auth/auth.service';
import { User } from '../../_foundation/models/security.models';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { TimeZoneChangeEditor } from '../../models/time-zones/time-zone.models';
import { cloneDeep } from 'lodash';
import { AppConfig } from '../../app/app.config';
import { DateTime } from 'luxon';
import { KendoGridDataConfig } from '../../_foundation/kendo/models/grid.models';
import {JackingFormInstanceSummary} from '../../models/jacking-v2/jacking.models';

@Injectable()
export class FieldPositioningService {
  config = AppConfig.settings;

  private readonly DAILY_LOG_KEY: string = 'daily_log_key';
  private state: State = {
    take: 50,
    skip: 0
  };

  private fieldPositioningTable: DbTable;

  constructor(
    private http: HttpClient,
    private dbContext: DbContext,
    private dateService: DateService,
    public vesselService: VesselService,
    private timeZoneService: TimeZoneService,
    private setupService: SetupService,
    private authService: AuthService,
    private dateParserService: DateParserService
  ) {
    this.fieldPositioningTable = this.dbContext.tables.find(t => t.name === 'FieldPositionings');
  }

  getState(): State {
    return this.state;
  }

  setState(state: State) {
    this.state = state;
  }

  getCurrentLogDay(): Date {
    const json = localStorage.getItem(this.DAILY_LOG_KEY);
    const date = JSON.parse(json);

    if (!date) {
      return new Date();
    } else {
      return new Date(date);
    }
  }

  async checkOutstandingFieldPositioning(dailyLogDate: DateTime): Promise<boolean> {
    // toMillis removes offset
    const dateLong = dailyLogDate.toMillis();
    const url = `${this.config.apiUrl}api/field-positioning/${dateLong}/checkoutstanding`;

    return await this.http.get<boolean>(url).toPromise();
  }

  setCurrentLogDay(date: Date) {
    localStorage.setItem(this.DAILY_LOG_KEY, JSON.stringify(date));
  }

  public async getActivitiesCompleteForDay(date?: PtLuxonDate): Promise<FieldPositioningEditor[]> {
    let activities = (await this.dbContext.getAll(this.fieldPositioningTable)) as FieldPositioningEditor[];

    for (let i = 0; i < activities.length; i++) {
      this.normalizeFieldPositioning(activities[i]);
      await this.deserializeFieldPositioningDates(activities[i]);
    }

    activities = activities.filter(
      a =>
        date.getDay() >= a.startDate.getDay() &&
        date.getDay() <= a.endDate.getDay()
    );

    return activities;
  }

  async getActivitiesForDateFromApi(dailyLogDateUnixMs: number): Promise<FieldPositioningEditor[]> {
    return this.http
      .get<FieldPositioningEditor[]>(`${AppConfig.settings.apiUrl}api/field-positioning/editors/${dailyLogDateUnixMs}/all`)
      .toPromise()
      .then(async (data: FieldPositioningEditor[]) => {
        // date object conversion
        data = await this.dateParserService.deserializeDates(data, true, this.timeZoneService);

        // custom date show logic for each record
        for (const activity of data) {
          this.normalizeFieldPositioning(activity);
          await this.deserializeFieldPositioningDates(activity);
        }

        return data;
      });
  }

  // gets activity data from the local stored field positionings
  public async getActivitiesForDateFromLocal(date: PtLuxonDate, endOfDay: PtLuxonDate, partialLoad?: boolean): Promise<FieldPositioningEditor[]> {
    let activities = (await this.dbContext.getAll(this.fieldPositioningTable)) as FieldPositioningEditor[];

    for (const activity of activities) {
      this.normalizeFieldPositioning(activity);
      // when validation, partialLoad = true, means faster validation as we don't load things we dont need
      if (partialLoad) {
        activity.startDate = await this.dateParserService.deserializePtLuxonDate(activity.startDate, this.timeZoneService);
        activity.endDate = await this.dateParserService.deserializePtLuxonDate(activity.endDate, this.timeZoneService);
      } else {
        await this.deserializeFieldPositioningDates(activity);
      }
    }

    // filter for the following:
    // where activity start date happens on the current date
    // where activity end date falls on the current date
    // where the current date falls between the start and end day, i.e an activity that spans 3+ days
    activities = activities.filter(a => a.startDate.dateTime >= date.dateTime && a.startDate.dateTime < endOfDay.dateTime
      || a.endDate.dateTime >= date.dateTime && a.endDate.dateTime < endOfDay.dateTime
      || date.dateTime >= a.startDate.dateTime && date.dateTime <= a.endDate.dateTime);

    return activities;
  }

  public async ValidateForDay(ptDate: PtLuxonDate): Promise<Boolean> {
    const endOfDay = await this.dateService.getEndOfDayLocally(ptDate.dateTime);

    return this.getActivitiesForDateFromLocal(ptDate, endOfDay, true).then(res => {
      return res.every(e => e.confirmed);
    });
  }

  generateDprDownloadLink(date: PtLuxonDate, id: number, isPortrait: boolean, isImplementer: boolean): string {
    const utcDate = this.dateParserService.removeOffsetFromLuxonDate(date.dateTime);
    const dateLong = utcDate.toMillis();

    // tslint:disable-next-line:max-line-length
    return `${this.config.apiUrl}api/field-positioning/${dateLong}/generatereport/${id}/${this.authService.currentUser<User>().id}/${isImplementer}/${isPortrait}`;
  }


  public async deserializeFieldPositioningDates(activity: FieldPositioningEditor) {
    // Setup "Display only dates"
    activity.pinnedTime = await this.dateParserService.deserializePtLuxonDate(activity.pinnedTime, this.timeZoneService);
    activity.showPinnedTime = this.dateService.setTimeStringFromPtLuxonDate(activity.pinnedTime);
    activity.showPinnedTimeFull = await this.dateService.setFullTimeString(activity.pinnedTime.dateTime);

    activity.startDate = await this.dateParserService.deserializePtLuxonDate(activity.startDate, this.timeZoneService);
    activity.showStartDate = await this.dateService.setFullTimeString(activity.startDate.dateTime);
    activity.startDay = await activity.startDate.getDay();

    activity.endDate = await this.dateParserService.deserializePtLuxonDate(activity.endDate, this.timeZoneService);
    activity.showEndDate = await this.dateService.setFullTimeString(activity.endDate.dateTime);
    activity.endDay = await activity.endDate.getDay();

    activity.lastModifiedDate = await this.dateParserService.deserializePtLuxonDate(activity.lastModifiedDate, this.timeZoneService);
    activity.showLastModifiedDate = await this.dateService.setFullTimeString(activity.lastModifiedDate.getDate());

    activity.status = activity.confirmed ? 'Complete' : 'Incomplete';
  }

  public async deleteAllLocalFieldPositionings() {
    await this.dbContext.removeAll(this.fieldPositioningTable);
  }

  public saveFieldPositioningLocally(fieldPositioningEditor: FieldPositioningEditor) {
    this.normalizeFieldPositioning(fieldPositioningEditor);
    this.dbContext.addOrUpdate(this.fieldPositioningTable, fieldPositioningEditor.id, fieldPositioningEditor);
  }

  public async saveFieldPositioningsLocally(fieldPositioningEditors: FieldPositioningEditor[]) {
    for (let i = 0; i < fieldPositioningEditors.length; i++) {
      this.saveFieldPositioningLocally(fieldPositioningEditors[i]);
    }
  }

  // Creates a new Jacking Activity linked to this operation if one isn't assigned.
  // Updates the currently linked activity & events
  // Returns the Jacking Activity in the promise
  public async createOrUpdateActivityForOperation(
    operationInstance: DailyLogOperationInstance,
    jackingActivity: JackingActivityEditor,
    currentLocationId: number = null,
  ): Promise<FieldPositioningEditor> {
    let fieldPositioning: FieldPositioningEditor;
    // Create Editor
    if (!jackingActivity.fieldPositioningEntityId) {
      fieldPositioning = new FieldPositioningEditor();

      fieldPositioning.localJackingActivityGuid = jackingActivity.guid;

      fieldPositioning.startDate = PtLuxonDate.assignPtLuxonDate(operationInstance.startTime);
      fieldPositioning.endDate = PtLuxonDate.assignPtLuxonDate(operationInstance.endTime);
      fieldPositioning = await this.setupService
        .getSetupSummary(fieldPositioning.startDate && fieldPositioning.startDate.dateTime ? fieldPositioning.startDate : new PtLuxonDate(), true)
        .then(summary => {
          return this.vesselService.getEditor().then(value => {
            fieldPositioning.clientName = summary.projectClientName;
            fieldPositioning.projectName = summary.projectName;
            fieldPositioning.projectId = summary.projectId;
            fieldPositioning.locationName = summary.locationName;
            fieldPositioning.locationId = summary.locationId;
            fieldPositioning.vesselName = value.name;
            fieldPositioning.vesselId = value.id;
            fieldPositioning.activity = summary.stageName;
            return fieldPositioning;
          });
        });
    } else {
      fieldPositioning = await this.getById(jackingActivity.fieldPositioningEntityId, jackingActivity.guid);
      if (fieldPositioning) {
        await this.deserializeFieldPositioningDates(fieldPositioning);
      }
    }

    // ensures that the location on the field positioning form stays correct when editing in implementer mode, as the locations
    // may have changed since it was originally created
    if (fieldPositioning) {
      fieldPositioning.locationId = currentLocationId;
      const location = await this.setupService.getLocationSummary(currentLocationId);
      fieldPositioning.locationName = location.name;
    }

    return fieldPositioning;
  }

  async getById(fieldPositioningEntityId: number, jackingActivityGuid): Promise<FieldPositioningEditor> {
    const activities = await this.dbContext.getAll(this.fieldPositioningTable);
    const selectedActivity = activities.find(a => a.id === fieldPositioningEntityId || a.localJackingActivityGuid === jackingActivityGuid);

    if (selectedActivity) {
      this.normalizeFieldPositioning(selectedActivity);
      // todo - check deserializeFieldPositioningDates actually mutates or i need to specifically return the activity from that method
      await this.deserializeFieldPositioningDates(selectedActivity);
    }

    return selectedActivity;
  }

  async CalculateMissingFields(editor: FieldPositioningEditor): Promise<ValidationResult> {
    const result = new ValidationResult();
    const isJackingV2 = !!editor.jackingFormInstanceId;

    if (this.isPropertyNull(editor.showPinnedTime)) {
      result.addError('Pinned Time', ErrorType.Error);
    }

    if (this.isPropertyNull(editor.activity)) {
      result.addError('Activity', ErrorType.Error);
    }

    if (this.isPropertyNull(editor.windSpeed)) {
      result.addError('Wind Speed', ErrorType.Error);
    }

    if (this.isPropertyNull(editor.windDirection)) {
      result.addError('Wind Direction', ErrorType.Error);
    }

    if (this.isPropertyNull(editor.seaHeight)) {
      result.addError('Sea Height', ErrorType.Error);
    }

    if (isJackingV2) {
      if (this.isPropertyNull(editor.currentDirection)) {
        result.addError('Current Direction', ErrorType.Error);
      }

      if (this.isPropertyNull(editor.currentRate)) {
        result.addError('Current Rate', ErrorType.Error);
      }
    } else {
      if (this.isPropertyNull(editor.seaDirection)) {
        result.addError('Sea Direction', ErrorType.Error);
      }

      if (this.isPropertyNull(editor.tideRate)) {
        result.addError('Tide Rate', ErrorType.Error);
      }

      if (this.isPropertyNull(editor.tideDirection)) {
        result.addError('Tide Direction', ErrorType.Error);
      }
    }

    if (this.isPropertyNull(editor.heading)) {
      result.addError('Heading', ErrorType.Error);
    }

    if (this.isPropertyNull(editor.requiredDistanceAsset)) {
      result.addError('Req\'d Distance to Asset', ErrorType.Error);
    }

    if (this.isPropertyNull(editor.actualDistanceAsset)) {
      result.addError('Actual Distance to Asset', ErrorType.Error);
    }

    this.normalizeFieldPositioning(editor);
    for (const leg of editor.legInfo.legs) {
      this.CalculateMissingFields_PerLeg(leg, result, `Leg ${leg.legNumber}`);
    }

    return result;
  }

  public getOutstandingFieldPositioningGridDataConfig(): KendoGridDataConfig {
      const url = `${AppConfig.settings.apiUrl}api/field-positioning/outstanding/grid/data`;
      return { name: 'field-positioning-outstanding-grid', url: url };
    }

  private async CalculateMissingFields_PerLeg(
    editor: FieldPositioningLegSummaryPerLegEditor,
    validationResult: ValidationResult,
    legName: string
  ) {

    if (this.isPropertyNull(editor.northing)) {
      validationResult.addError('Northing, ' + legName, ErrorType.Error);
    }

    if (this.isPropertyNull(editor.easting)) {
      validationResult.addError('Easting, ' + legName, ErrorType.Error);
    }

    if (this.isPropertyNull(editor.penetration)) {
      validationResult.addError('Penetration, ' + legName, ErrorType.Error);
    }

    if (this.isPropertyNull(editor.finalLegExt)) {
      validationResult.addError('Final Leg Ext, ' + legName, ErrorType.Error);
    }

    if (this.isPropertyNull(editor.finalLegLoad)) {
      validationResult.addError('Final Leg Load, ' + legName, ErrorType.Error);
    }

  }

  private isPropertyNull(property: any): boolean {
    if (typeof property === 'number') {
      return property === null || property === undefined;
    } else if (!property) {
      return true;
    } else {
      return false;
    }
  }

  private normalizeFieldPositioning(editor: FieldPositioningEditor) {
    if (!editor.legInfo) {
      editor.legInfo = new FieldPositioningLegSummaryEditor();
    }

    normalizeFieldPositioningLegSummary(editor.legInfo, MAX_FIELD_POSITIONING_LEGS);
  }
}



// WEBPACK FOOTER //
// ./src/services/field-positioning/field-positioning.service.ts